import { useCallback, useState } from "react";
import { InterviewEmailPreview } from "@/components/interview-email-preview";
import { InterviewerPicker } from "@/components/interviewer-picker";
import type { AssignmentOption, PersonOption } from "@/types/move-application";

export interface InterviewSchedulingSectionProps {
  assignmentOptions: AssignmentOption[];
  people?: PersonOption[];
  isLoading?: boolean;

  // Interviewer state
  interviewerId: string;
  setInterviewerId: (id: string) => void;
  selectedGroupId: string | null;
  setSelectedGroupId: (id: string | null) => void;
  assignmentMode: "automatic" | "manual";
  setAssignmentMode: (mode: "automatic" | "manual") => void;

  // Email state
  sendEmailToCandidate: boolean;
  setSendEmailToCandidate: (v: boolean) => void;
  emailStageName: string;
  setEmailStageName: (v: string) => void;
  emailSubject: string;
  setEmailSubject: (v: string) => void;
  emailIntroText: string;
  setEmailIntroText: (v: string) => void;
  emailExtraBody: string;
  setEmailExtraBody: (v: string) => void;
  emailButtonText: string;
  setEmailButtonText: (v: string) => void;
  emailClosingText: string;
  setEmailClosingText: (v: string) => void;

  // Context for email preview
  candidateName: string;
  interviewerName?: string;
  interviewerEmail?: string;
  durationMinutes?: number;
  stageDescription?: string;
}

export function InterviewSchedulingSection({
  assignmentOptions,
  people,
  isLoading = false,
  interviewerId,
  setInterviewerId,
  selectedGroupId,
  setSelectedGroupId,
  assignmentMode,
  setAssignmentMode,
  sendEmailToCandidate,
  setSendEmailToCandidate,
  emailStageName,
  setEmailStageName,
  emailSubject,
  setEmailSubject,
  emailIntroText,
  setEmailIntroText,
  emailExtraBody,
  setEmailExtraBody,
  emailButtonText,
  setEmailButtonText,
  emailClosingText,
  setEmailClosingText,
  candidateName,
  durationMinutes = 60,
  stageDescription = "",
}: InterviewSchedulingSectionProps) {
  const [selectedOption, setSelectedOption] = useState<AssignmentOption | undefined>();
  const [hasNoSchedulingLink, setHasNoSchedulingLink] = useState(false);

  const handleSelectionChange = useCallback(
    (info: {
      selectedOption: AssignmentOption | undefined;
      resolvedLink: string | null;
      hasNoSchedulingLink: boolean;
    }) => {
      setSelectedOption(info.selectedOption);
      setHasNoSchedulingLink(info.hasNoSchedulingLink);
    },
    [],
  );

  return (
    <div className="space-y-4 py-4">
      <InterviewerPicker
        assignmentOptions={assignmentOptions}
        people={people}
        isLoading={isLoading}
        interviewerId={interviewerId}
        setInterviewerId={setInterviewerId}
        selectedGroupId={selectedGroupId}
        setSelectedGroupId={setSelectedGroupId}
        assignmentMode={assignmentMode}
        setAssignmentMode={setAssignmentMode}
        durationMinutes={durationMinutes}
        showCopyLink
        showSendEmail
        sendEmail={sendEmailToCandidate}
        onSendEmailChange={setSendEmailToCandidate}
        onSelectionChange={handleSelectionChange}
      />

      {/* Email preview — only shown when sending email and has scheduling link */}
      {sendEmailToCandidate && selectedOption && !hasNoSchedulingLink && (
        <InterviewEmailPreview
          candidateName={candidateName}
          subject={emailSubject}
          onSubjectChange={setEmailSubject}
          introText={emailIntroText}
          onIntroTextChange={setEmailIntroText}
          stageName={emailStageName}
          onStageNameChange={setEmailStageName}
          interviewerName={selectedOption?.label || "Entrevistador"}
          interviewerEmail={selectedOption?.email || ""}
          durationMinutes={durationMinutes}
          stageDescription={stageDescription}
          extraBody={emailExtraBody}
          onExtraBodyChange={setEmailExtraBody}
          buttonText={emailButtonText}
          onButtonTextChange={setEmailButtonText}
          closingText={emailClosingText}
          onClosingTextChange={setEmailClosingText}
        />
      )}
    </div>
  );
}
